import React, { useEffect, useState } from "react";
import { Alert, Text, View } from "react-native";
import { Edit3, Plus, Save, Tags, Trash2 } from "lucide-react-native";
import { api } from "../api/client";
import { ActionMenu, Badge, Button, Card, DataTable, Field, ScreenHeader, Select, Toolbar } from "../components/UI";
import { styles } from "../styles";

export function CategoriesScreen({ user }) {
  const [data, setData] = useState({ main_categories: [], sub_categories: [] });
  const [main, setMain] = useState({ name: "" });
  const [sub, setSub] = useState({ main_category_id: "", name: "" });

  const isAdmin = user && (String(user.role || "").toLowerCase().includes("admin"));

  const load = () => api.categories().then(setData).catch((error) => Alert.alert("Categories", error.message));

  useEffect(() => {
    load();
  }, []);

  const saveMain = async () => {
    if (!main.name?.trim()) return Alert.alert("Categories", "Main category name is required.");
    try {
      await api.saveMainCategory(main);
      setMain({ name: "" });
      load();
    } catch (error) {
      Alert.alert("Categories", error.message);
    }
  };

  const removeMain = async (id, name) => {
    if (!confirm(`Are you sure you want to delete main category "${name}"?`)) return;
    try {
      await api.deleteMainCategory(id);
      load();
    } catch (error) {
      Alert.alert("Categories", error.message);
    }
  };

  const saveSub = async () => {
    if (!sub.main_category_id) return Alert.alert("Categories", "Please select a parent main category.");
    if (!sub.name?.trim()) return Alert.alert("Categories", "Sub category name is required.");
    try {
      await api.saveSubCategory(sub);
      setSub({ main_category_id: "", name: "" });
      load();
    } catch (error) {
      Alert.alert("Categories", error.message);
    }
  };

  const removeSub = async (id, name) => {
    if (!confirm(`Are you sure you want to delete sub category "${name}"?`)) return;
    try {
      await api.deleteSubCategory(id);
      load();
    } catch (error) {
      Alert.alert("Categories", error.message);
    }
  };

  return (
    <View style={styles.page}>
      <ScreenHeader title="Product Categories" subtitle="Maintain main and sub category master data (Global and Account-specific)" />

      <View style={styles.row}>
        <Card style={{ flex: 1, minWidth: 320 }}>
          <Text style={styles.h2}>{main.id ? "Edit Main Category" : "Add Main Category"}</Text>
          <Field
            label="Category Name *"
            value={main.name}
            onChangeText={(name) => setMain({ ...main, name })}
            placeholder="e.g. Surveillance Cameras"
          />
          <Toolbar>
            <Button icon={Save} onPress={saveMain}>
              {main.id ? "Update Main Category" : "Save Main Category"}
            </Button>
            {main.id ? (
              <Button icon={Plus} variant="secondary" onPress={() => setMain({ name: "" })}>
                Cancel
              </Button>
            ) : null}
          </Toolbar>
        </Card>

        <Card style={{ flex: 1, minWidth: 320 }}>
          <Text style={styles.h2}>{sub.id ? "Edit Sub Category" : "Add Sub Category"}</Text>
          <Select
            label="Parent Main Category *"
            value={sub.main_category_id}
            onChangeText={(v) => setSub({ ...sub, main_category_id: v })}
          >
            <option value="">Select Main Category...</option>
            {data.main_categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </Select>
          <Field
            label="Sub Category Name *"
            value={sub.name}
            onChangeText={(name) => setSub({ ...sub, name })}
            placeholder="e.g. IP Cameras"
          />
          <Toolbar>
            <Button icon={Save} onPress={saveSub}>
              {sub.id ? "Update Sub Category" : "Save Sub Category"}
            </Button>
            {sub.id ? (
              <Button icon={Plus} variant="secondary" onPress={() => setSub({ main_category_id: "", name: "" })}>
                Cancel
              </Button>
            ) : null}
          </Toolbar>
        </Card>
      </View>

      <View style={styles.row}>
        <View style={{ flex: 1, minWidth: 320 }}>
          <Card>
            <Text style={styles.h2}>Main Categories List</Text>
            <DataTable
              actionsWidth={56}
              columns={[
                { key: "name", label: "Category Name", flex: 2, style: { fontWeight: "700" } },
                {
                  key: "scope",
                  label: "Scope",
                  flex: 1,
                  render: (r) => (
                    <Badge variant={!r.created_by_user_id ? "secondary" : "info"}>
                      {!r.created_by_user_id ? "GLOBAL" : (Number(r.created_by_user_id) === Number(user?.id) ? "MY ACCOUNT" : "ACCOUNT")}
                    </Badge>
                  )
                }
              ]}
              rows={data.main_categories}
              renderActions={(row) => {
                const isOwner = Number(row.created_by_user_id) === Number(user?.id);
                const canManage = isAdmin || isOwner;
                if (!canManage) return null;
                return (
                  <ActionMenu
                    actions={[
                      { label: "Edit Category", icon: Edit3, onPress: () => setMain({ id: row.id, name: row.name }) },
                      { label: "Delete Category", icon: Trash2, variant: "danger", onPress: () => removeMain(row.id, row.name) }
                    ]}
                  />
                );
              }}
            />
          </Card>
        </View>

        <View style={{ flex: 1, minWidth: 320 }}>
          <Card>
            <Text style={styles.h2}>Sub Categories List</Text>
            <DataTable
              actionsWidth={56}
              columns={[
                { key: "name", label: "Sub Category Name", flex: 1.6, style: { fontWeight: "700" } },
                {
                  key: "main_category_id",
                  label: "Main Category",
                  flex: 1.4,
                  render: (r) => data.main_categories.find((m) => String(m.id) === String(r.main_category_id))?.name || `ID: ${r.main_category_id}`
                },
                {
                  key: "scope",
                  label: "Scope",
                  flex: 1,
                  render: (r) => (
                    <Badge variant={!r.created_by_user_id ? "secondary" : "info"}>
                      {!r.created_by_user_id ? "GLOBAL" : (Number(r.created_by_user_id) === Number(user?.id) ? "MY ACCOUNT" : "ACCOUNT")}
                    </Badge>
                  )
                }
              ]}
              rows={data.sub_categories}
              renderActions={(row) => {
                const isOwner = Number(row.created_by_user_id) === Number(user?.id);
                const canManage = isAdmin || isOwner;
                if (!canManage) return null;
                return (
                  <ActionMenu
                    actions={[
                      { label: "Edit Sub Category", icon: Edit3, onPress: () => setSub({ id: row.id, main_category_id: row.main_category_id, name: row.name }) },
                      { label: "Delete Sub Category", icon: Trash2, variant: "danger", onPress: () => removeSub(row.id, row.name) }
                    ]}
                  />
                );
              }}
            />
          </Card>
        </View>
      </View>
    </View>
  );
}
